import { useEffect, useMemo, useState } from 'react';
import { useLocation, matchPath, PathMatch, generatePath } from 'react-router-dom';
import { RoutePath as rp } from 'src/router';

import { useSelector, useDispatch } from 'src/store';
import { setBreadcrumbsNames } from 'src/store/slices/breadcrumbsSlice';
import { breadcrumbsNamesMapSelector } from 'src/store/selectors/breadcrumbsSelector';

import { Breadcrumb, Breadcrumbs } from '@itm/shared-frontend/lib/components';

type BreadcrumbConfig = {
  name: string | null | undefined;
  parentPath: string | null;
};

type ExtendedBreadcrumbsConfig = BreadcrumbConfig & Pick<PathMatch<string>, 'pathname'>;

type BreadcrumbConfigMap = {
  [key: string]: BreadcrumbConfig;
};

type UseSetBreadcrumbsNamesProps = Readonly<{
  routePath: string;
  value: string | null | undefined;
  isClearOnUnmount?: boolean;
}>;

export const useSetBreadcrumbsNames = ({ routePath, value, isClearOnUnmount = true }: UseSetBreadcrumbsNamesProps) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (!value) return;
    dispatch(setBreadcrumbsNames({ [routePath]: value }));
  }, [routePath, value, dispatch]);

  useEffect(
    () => () => {
      if (!isClearOnUnmount) return;
      dispatch(setBreadcrumbsNames({ [routePath]: null }));
    },
    [routePath, isClearOnUnmount, dispatch],
  );
};

const getBreadcrumbs = (pathToMatch: string, breadcrumbsConfig: BreadcrumbConfigMap): Breadcrumb[] => {
  const matchedList = Object.keys(breadcrumbsConfig).reduce<PathMatch<string>[]>((acc, route) => {
    const matched = matchPath(route, pathToMatch);
    return matched ? [...acc, matched] : acc;
  }, []);

  if (!matchedList.length) return [];

  const pathMatch =
    // try to find the full match
    matchedList.find((m) => !m.pattern.path.endsWith('/*')) ||
    // if not found, find the longest wildcard match
    matchedList.toSorted((a, b) => b.pattern.path.length - a.pattern.path.length)[0];

  const routeParams = pathMatch.params;

  let revertedBreadcrumbsList: Breadcrumb[] = [];
  let extendedConfig: ExtendedBreadcrumbsConfig | null = {
    pathname: pathMatch.pathname,
    ...breadcrumbsConfig[pathMatch.pattern.path],
  };

  while (extendedConfig) {
    const { name, pathname, parentPath }: ExtendedBreadcrumbsConfig = extendedConfig;

    if (name) {
      revertedBreadcrumbsList.push({ name, to: pathname });
    } else {
      revertedBreadcrumbsList = [];
    }

    extendedConfig = parentPath
      ? { ...breadcrumbsConfig[parentPath], pathname: generatePath(parentPath, routeParams) }
      : null;
  }

  return revertedBreadcrumbsList.reverse();
};

function BreadcrumbsTrail() {
  const { pathname } = useLocation();
  const bcNamesMap = useSelector(breadcrumbsNamesMapSelector);
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([]);

  const isHidden = useMemo(
    () =>
      Boolean(
        matchPath(rp.root, pathname) ||
        matchPath(rp.loginRedirect, pathname) ||
        matchPath(rp.permissionDenied, pathname),
      ),
    [pathname],
  );

  const config: BreadcrumbConfigMap = useMemo(
    () => ({
      [rp.root]: { name: 'FlexPortal', parentPath: null },

      // CONFIGURATION
      [rp.configurationRoot]: { name: 'Configuration', parentPath: rp.root },
      // Configuration / General Settings
      [`${rp.configurationGeneralSettingsRoot}/*`]: { name: 'General Settings', parentPath: rp.configurationRoot },
      // Configuration / Scheme Configuration
      [rp.configurationSchemeList]: { name: 'Scheme Configuration', parentPath: rp.configurationRoot },
      [rp.configurationSchemeCreateRoot]: {
        name: 'Add new scheme configuration',
        parentPath: rp.configurationSchemeList,
      },
      // Configuration / Scheme Configuration / View
      [rp.configurationSchemeViewRoot]: {
        name: bcNamesMap[rp.configurationSchemeViewRoot],
        parentPath: rp.configurationSchemeList,
      },
      [rp.configurationSchemeViewConfiguration]: {
        name: 'Configuration',
        parentPath: rp.configurationSchemeViewRoot,
      },
      [rp.configurationSchemeViewAudit]: { name: 'Audit', parentPath: rp.configurationSchemeViewRoot },
      // Configuration / Scheme Configuration / Edit
      [rp.configurationSchemeEditRoot]: {
        name: bcNamesMap[rp.configurationSchemeEditRoot],
        parentPath: rp.configurationSchemeList,
      },
      [rp.configurationSchemeEditConfiguration]: {
        name: 'Configuration',
        parentPath: rp.configurationSchemeEditRoot,
      },
      [rp.configurationSchemeEditAudit]: { name: 'Audit', parentPath: rp.configurationSchemeEditRoot },
      // Configuration / Employer Configuration
      [rp.configurationEmployerRoot]: {
        name: 'Employer configuration',
        parentPath: rp.configurationRoot,
      },
      [rp.configurationEmployerPolicies]: {
        name: 'Policies',
        parentPath: rp.configurationEmployerRoot,
      },
      [rp.configurationEmployerEntities]: {
        name: 'Entities',
        parentPath: rp.configurationEmployerRoot,
      },
      [rp.configurationEmployerAudit]: {
        name: 'Audit',
        parentPath: rp.configurationEmployerRoot,
      },
      [rp.configurationEmployerAuditDetails]: {
        name: bcNamesMap[rp.configurationEmployerAuditDetails],
        parentPath: rp.configurationEmployerAudit,
      },
      [rp.configurationEmployerPolicyCreate]: {
        name: 'Add New Policy',
        parentPath: rp.configurationEmployerPolicies,
      },
      [rp.configurationEmployerPolicyEditRoot]: {
        name: bcNamesMap[rp.configurationEmployerPolicyEditRoot],
        parentPath: rp.configurationEmployerPolicies,
      },
      [rp.configurationEmployerPolicyEditDetails]: {
        name: 'General Details',
        parentPath: rp.configurationEmployerPolicyEditRoot,
      },
      [rp.configurationEmployerPolicyEditParameters]: {
        name: 'Parameters',
        parentPath: rp.configurationEmployerPolicyEditRoot,
      },
      [`${rp.configurationEmployerPolicyEditAudit}/*`]: {
        name: 'Audit',
        parentPath: rp.configurationEmployerPolicyEditRoot,
      },
      [rp.configurationEmployerPolicyViewRoot]: {
        name: bcNamesMap[rp.configurationEmployerPolicyViewRoot],
        parentPath: rp.configurationEmployerPolicies,
      },
      [rp.configurationEmployerPolicyViewParameters]: {
        name: 'Parameters',
        parentPath: rp.configurationEmployerPolicyViewRoot,
      },
      [rp.configurationEmployerEntityCreate]: {
        name: 'Add new entity',
        parentPath: rp.configurationEmployerEntities,
      },
      [rp.configurationEmployerEntityEdit]: {
        name: bcNamesMap[rp.configurationEmployerEntityEdit],
        parentPath: rp.configurationEmployerEntities,
      },

      // DATA MANAGEMENT
      [rp.dataManagementRoot]: { name: 'Data Management', parentPath: rp.root },
      // Data Management / Processes
      [`${rp.dataManagementProcessCreateRoot}/*`]: { name: 'New Monthly Process', parentPath: rp.dataManagementRoot },
      [rp.dataManagementProcessSummary]: { name: 'Monthly Process summary', parentPath: rp.dataManagementRoot },
      // Data Management / Processes / Summary / History
      [rp.dataManagementProcessSummaryHistoryList]: {
        name: 'Process History',
        parentPath: rp.dataManagementProcessSummary,
      },
      [rp.dataManagementProcessSummaryHistoryDetailsRoot]: {
        name: bcNamesMap[rp.dataManagementProcessSummaryHistoryDetailsRoot],
        parentPath: rp.dataManagementProcessSummaryHistoryList,
      },
      [rp.dataManagementProcessSummaryHistoryDetailsMemberChecks]: {
        name: 'Member Checks',
        parentPath: rp.dataManagementProcessSummaryHistoryDetailsRoot,
      },
      [rp.dataManagementProcessSummaryHistoryDetailsBenefitAnalysis]: {
        name: 'Benefit Analysis',
        parentPath: rp.dataManagementProcessSummaryHistoryDetailsRoot,
      },
      [rp.dataManagementProcessSummaryHistoryDetailsAudit]: {
        name: 'Audit',
        parentPath: rp.dataManagementProcessSummaryHistoryDetailsRoot,
      },
      [rp.dataManagementProcessSummaryHistoryDetailsUnderwriting]: {
        name: 'Underwriting Status',
        parentPath: rp.dataManagementProcessSummaryHistoryDetailsRoot,
      },

      // REPORTING
      [rp.reportingRoot]: { name: 'Reporting', parentPath: rp.root },

      // Reporting / Invoice History
      [rp.reportingInvoiceHistoryList]: { name: 'Invoice History', parentPath: rp.reportingRoot },
      [rp.reportingInvoiceHistoryDetails]: {
        name: bcNamesMap[rp.reportingInvoiceHistoryDetails],
        parentPath: rp.reportingInvoiceHistoryList,
      },

      // Reporting / Templates
      [rp.reportingTemplatesList]: { name: 'All Templates', parentPath: rp.reportingRoot },
      // Reporting / Templates / Create
      [rp.reportingTemplateCreate]: { name: 'Add New Template', parentPath: rp.reportingTemplatesList },
      // Reporting / Templates / View
      [rp.reportingTemplateViewRoot]: {
        name: bcNamesMap[rp.reportingTemplateViewRoot],
        parentPath: rp.reportingTemplatesList,
      },
      [rp.reportingTemplateViewDetails]: { name: 'General Details', parentPath: rp.reportingTemplateViewRoot },
      [rp.reportingTemplateViewContent]: { name: 'Content', parentPath: rp.reportingTemplateViewRoot },
      [rp.reportingTemplateViewHistory]: { name: 'Version History', parentPath: rp.reportingTemplateViewRoot },
      // Reporting / Templates / Edit
      [rp.reportingTemplateEditRoot]: {
        name: bcNamesMap[rp.reportingTemplateEditRoot],
        parentPath: rp.reportingTemplatesList,
      },
      [rp.reportingTemplateEditDetails]: { name: 'General Details', parentPath: rp.reportingTemplateEditRoot },
      [rp.reportingTemplateEditContent]: { name: 'Content', parentPath: rp.reportingTemplateEditRoot },
      [rp.reportingTemplateEditHistory]: { name: 'Version History', parentPath: rp.reportingTemplateEditRoot },

      // PRODUCT INFO
      [rp.productInfo]: { name: 'Product Information', parentPath: rp.root },
    }),
    [bcNamesMap],
  );

  // Debounce breadcrumbs update to avoid unnecessary updates by redirects
  useEffect(() => {
    if (isHidden) return;

    const delayTimeout = setTimeout(() => {
      setBreadcrumbs(getBreadcrumbs(pathname, config));
    }, 50);
    return () => {
      clearTimeout(delayTimeout);
    };
  }, [config, isHidden, pathname]);

  return isHidden ? null : <Breadcrumbs breadcrumbs={breadcrumbs} />;
}

export default BreadcrumbsTrail;
